import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExpandLess,
  ExpandMore,
  Info as InfoIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  Label as LabelIcon,
  LiveHelp as LiveHelpIcon,
  Description as DescriptionIcon,
  SpaceDashboard as SpaceDashboardIcon,
  Groups3 as Groups3Icon,
  SupportAgent as SupportAgentIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../hooks/redux";
import { logout } from "../../store/slices/authSlice";
import Footer from "./Footer";

const drawerWidth = 240;

const menuItems = [
  { text: "Dashboard", icon: <SpaceDashboardIcon />, path: "/dashboard" },
  { text: "Categories", icon: <CategoryIcon />, path: "/categories" },
  { text: "Products", icon: <InventoryIcon />, path: "/products" },
  { text: "Orders", icon: <OrdersIcon />, path: "/orders" },
  { text: "Agents", icon: <SupportAgentIcon />, path: "/agent" },
  { text: "Groups", icon: <Groups3Icon />, path: "/groups" },
  { text: "Product Master", icon: <LabelIcon />, path: "/product-master" },
];

const settingsSubItems = [
  { text: "About Info", icon: <InfoIcon />, path: "/settings" },
  { text: "Offer Banner", icon: <InventoryIcon />, path: "/banners" },
  { text: "Change Password", icon: <SecurityIcon />, path: "/changepassword" },
  { text: "Manage Permissions", icon: <SecurityIcon />, path: "/manage-permissions" },
];

const cmsSubItems = [
  // { text: "Privacy Policy", icon: <PolicyIcon />, path: "/privacy-policy" },
  { text: "Terms & Conditions", icon: <PolicyIcon />, path: "/terms-and-conditions" },
  { text: "About Reward", icon: <InfoIcon />, path: "/about-reward" },
  { text: "FAQs", icon: <LiveHelpIcon />, path: "/faqs" },
];

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cmsOpen, setCmsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const prevPathnameRef = useRef(location.pathname);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
    navigate("/login");
    // Reload the page to ensure proper logout (same as Angular version)
    window.location.reload();
  };

  const handleChangePassword = () => {
    handleMenuClose();
    navigate("/changepassword");
  };

  const handleAboutInfo = () => {
    handleMenuClose();
    navigate("/settings");
  };

  const handleSettingsClick = () => {
    setSettingsOpen(!settingsOpen);
    if (!settingsOpen && cmsOpen) setCmsOpen(false); // closing CMS when opening Settings
  };
  const handleCmsClick = () => {
    setCmsOpen(!cmsOpen);
    if (!cmsOpen && settingsOpen) setSettingsOpen(false); // closing Settings when opening CMS
  };

  const handleMainNav = (path: string) => {
    navigate(path);
    if (settingsOpen) setSettingsOpen(false);
    if (cmsOpen) setCmsOpen(false);
  };

  const settingsPaths = settingsSubItems.map(si => si.path);
  const cmsPaths = cmsSubItems.map(ci => ci.path);
  const isSettingsActive = settingsPaths.includes(location.pathname);
  const isCmsActive = cmsPaths.includes(location.pathname);

  // Auto-close settings and CMS drawers when navigating to non-settings or non-CMS pages
  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;

    // Auto-open the submenu for the active route (but do NOT auto-close the other one)
    if (isSettingsActive && !settingsOpen) setSettingsOpen(true);
    if (isCmsActive && !cmsOpen) setCmsOpen(true);

    // Auto-close a submenu only when navigating completely away from its routes
    if (settingsOpen && !isSettingsActive && settingsPaths.includes(prevPathname)) {
      setSettingsOpen(false);
    }
    if (cmsOpen && !isCmsActive && cmsPaths.includes(prevPathname)) {
      setCmsOpen(false);
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname, isSettingsActive, isCmsActive, settingsOpen, cmsOpen, settingsPaths, cmsPaths]);

  const drawer = (
    <Box>
      {/* Logo always at the very top */}
      <Box
        sx={{ p: 1.5, pb: 0, textAlign: "center", cursor: "pointer" }}
        onClick={() => handleMainNav("/dashboard")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleMainNav("/dashboard");
        }}
        aria-label="Go to dashboard"
      >
        <Box
          component="img"
          src={process.env.PUBLIC_URL + "/logo.svg"}
          alt="Rezlive Logo"
          sx={{
            height: 60,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            mb: 0.5,
            backgroundColor: "transparent",
            borderRadius: 0,
            display: "inline-block",
          }}
        />
      </Box>
      {/* Close Sidebar Button for all screens */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          p: 0.5,
          pb: 0,
        }}
      >
        <IconButton aria-label="close sidebar" onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ backgroundColor: "rgba(255,255,255,0.1)", my: 1 }} />
      <List sx={{ mt: 0 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            onClick={() => handleMainNav(item.path)}
            sx={{
              backgroundColor:
                location.pathname === item.path
                  ? "rgba(255,255,255,0.1)"
                  : "transparent",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "white" }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} sx={{ color: "white" }} />
          </ListItemButton>
        ))}

        {/* CMS Management with submenu */}
        <ListItemButton
          onClick={handleCmsClick}
          sx={{
            backgroundColor: isCmsActive
              ? "rgba(255,255,255,0.1)"
              : "transparent",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <ListItemIcon sx={{ color: "white" }}>
            <DescriptionIcon />
          </ListItemIcon>
          <ListItemText primary="CMS" sx={{ color: "white" }} />
          {cmsOpen ? (
            <ExpandLess sx={{ color: "white" }} />
          ) : (
            <ExpandMore sx={{ color: "white" }} />
          )}
        </ListItemButton>
        <Collapse in={cmsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {cmsSubItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => { if (settingsOpen) setSettingsOpen(false); navigate(item.path); }}
                sx={{
                  pl: 4,
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(255,255,255,0.1)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "white" }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} sx={{ color: "white" }} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Settings with submenu */}
        <ListItemButton
          onClick={handleSettingsClick}
          sx={{
            backgroundColor: isSettingsActive
              ? "rgba(255,255,255,0.1)"
              : "transparent",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <ListItemIcon sx={{ color: "white" }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Settings" sx={{ color: "white" }} />
          {settingsOpen ? (
            <ExpandLess sx={{ color: "white" }} />
          ) : (
            <ExpandMore sx={{ color: "white" }} />
          )}
        </ListItemButton>
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {settingsSubItems.map((item) => (
              <ListItemButton
                key={item.text}
                onClick={() => { if (cmsOpen) setCmsOpen(false); navigate(item.path); }}
                sx={{
                  pl: 4,
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(255,255,255,0.1)"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "white" }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} sx={{ color: "white" }} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: drawerOpen ? { sm: `calc(100% - ${drawerWidth}px)` } : "100%",
          ml: drawerOpen ? { sm: `${drawerWidth}px` } : 0,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Welcome to Rezlive
          </Typography>
          <IconButton color="inherit">
            <NotificationsIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            sx={{
              p: 0.5,
              border: "2px solid rgba(255, 255, 255, 0.2)",
              transition: "all 0.2s ease",
              "&:hover": {
                border: "2px solid rgba(255, 255, 255, 0.4)",
                transform: "scale(1.05)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              <AccountCircleIcon />
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: drawerOpen ? { sm: drawerWidth } : 0,
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="persistent"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: drawerOpen ? { sm: `calc(100% - ${drawerWidth}px)` } : "100%",
          mt: 8,
        }}
      >
        <Box sx={{ flexGrow: 1, p: 3 }}>{children}</Box>
        <Footer />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            "& .MuiMenuItem-root": {
              py: 1.5,
              px: 2,
              fontSize: "0.875rem",
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleLogout}
          sx={{
            color: "#d32f2f",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LogoutIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
            Logout
          </Box>
        </MenuItem>
        <MenuItem
          onClick={handleChangePassword}
          sx={{
            color: "#1976d2",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityIcon sx={{ fontSize: 20, color: "#1976d2" }} />
            Change Password
          </Box>
        </MenuItem>
        <MenuItem
          onClick={handleAboutInfo}
          sx={{
            color: "#455a64",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: "rgba(69, 90, 100, 0.08)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoIcon sx={{ fontSize: 20, color: "#455a64" }} />
            About Info
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MainLayout;
